import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { forwardRef, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BADGE, FONT_SIZE, INK, MOTION, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import { CountBadge } from "./CountBadge";
import type { DropdownTriggerVariant } from "./types";

type Props = {
	variant: DropdownTriggerVariant;
	open: boolean;
	/** id of the panel's listbox — wired to `aria-controls` (L10). */
	controlsId?: string;
	onToggle: (event: MouseEvent | KeyboardEvent) => void;
	label?: string;
	required?: boolean;
	errorText?: string;
	disabled?: boolean;
	fullWidth?: boolean;
	multiple: boolean;
	/** The ids currently selected — mirrored into hidden MenuItems so MUI never warns "out of range". */
	valueIds: string[];
	renderDisplay: (value: string | string[]) => ReactNode;
	hasSelection: boolean;
	/** Render the display (the empty-selection placeholder) even with nothing selected, keeping the label notched. */
	displayEmpty?: boolean;
	clearable?: boolean;
	onClear?: () => void;
	/** Pill/button variant: count shown in the tinted badge. */
	count?: number;
	formControlSx?: SxProps<Theme>;
	selectSx?: SxProps<Theme>;
	id?: string;
	dataTestId?: string;
};

const Caret = ({ open, className }: { open: boolean; className?: string }) => (
	<ExpandMoreIcon
		className={className}
		sx={{
			color: NEUTRAL[500],
			transition: `transform ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
			transform: open ? "rotate(180deg)" : "none",
			pointerEvents: "none",
			"@media (prefers-reduced-motion: reduce)": { transition: "none" },
		}}
	/>
);

const OPEN_KEYS = new Set(["Enter", " ", "ArrowDown", "ArrowUp"]);

/**
 * The closed-state control. `field` reproduces the classic MUI outlined + floating-label look via a
 * neutralized `Select` shell (menu never opens — the panel is our own Popper); `pill`/`button` are the
 * lighter opt-in toolbar variants. Selection is passed in as `valueIds`, so the trigger holds no state.
 */
export const DropdownTrigger = forwardRef<HTMLDivElement, Props>(function DropdownTrigger(props, ref) {
	const { t } = useTranslation();
	const {
		variant,
		open,
		controlsId,
		onToggle,
		label,
		required,
		errorText,
		disabled,
		fullWidth,
		multiple,
		valueIds,
		renderDisplay,
		hasSelection,
		displayEmpty,
		clearable,
		onClear,
		count,
		formControlSx,
		selectSx,
		id,
		dataTestId,
	} = props;

	const handleKeyDown = (event: KeyboardEvent) => {
		if (OPEN_KEYS.has(event.key)) {
			event.preventDefault();
			onToggle(event);
		}
	};

	const clearButton = clearable && hasSelection && (
		<IconButton
			size="small"
			edge="end"
			aria-label={t("system.dropdown.clear")}
			onMouseDown={(event) => event.stopPropagation()}
			onClick={(event) => {
				event.stopPropagation();
				onClear?.();
			}}
		>
			<CloseIcon fontSize="small" />
		</IconButton>
	);

	if (variant === "pill" || variant === "button") {
		const rounded = variant === "pill";
		return (
			<Box
				ref={ref}
				id={id}
				data-testid={dataTestId}
				role="combobox"
				aria-expanded={open}
				aria-haspopup="listbox"
				aria-controls={open ? controlsId : undefined}
				tabIndex={disabled ? -1 : 0}
				onClick={disabled ? undefined : onToggle}
				onKeyDown={disabled ? undefined : handleKeyDown}
				sx={[
					(theme) => ({
						display: "inline-flex",
						alignItems: "center",
						gap: 1,
						px: 1.25,
						py: 0.75,
						cursor: disabled ? "default" : "pointer",
						opacity: disabled ? 0.6 : 1,
						backgroundColor: SURFACE,
						border: `1px solid ${open ? theme.palette.primary.main : NEUTRAL[200]}`,
						borderRadius: rounded ? BADGE.PILL_RADIUS_FULL : 2,
						fontSize: FONT_SIZE.SECONDARY,
						color: INK.SECONDARY,
						transition: `border-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
						"@media (hover: hover) and (pointer: fine)": { "&:hover": { borderColor: theme.palette.primary.main } },
					}),
					// The pill/button variants used to DROP `selectSx`, so a caller had no way to restyle the
					// trigger's own type (a pill that should read as an uppercase chip, say). Last in the array
					// so the caller wins, exactly as it does on the field variant below.
					...(Array.isArray(selectSx) ? selectSx : [selectSx]),
				]}
			>
				{label && <Box component="span">{label}</Box>}
				{typeof count === "number" && count > 0 && <CountBadge count={count} />}
				{/* Single-select pill/button reflects the chosen value in the closed trigger (parity with the field variant). */}
				{!multiple && hasSelection ? renderDisplay(valueIds[0] ?? "") : !hasSelection && (!label || displayEmpty) ? renderDisplay(multiple ? [] : "") : null}
				<Caret open={open} />
			</Box>
		);
	}

	return (
		<FormControl
			ref={ref}
			id={id}
			data-testid={dataTestId}
			size="small"
			required={required}
			error={!!errorText}
			fullWidth={fullWidth}
			disabled={disabled}
			onClick={disabled ? undefined : onToggle}
			onKeyDown={disabled ? undefined : handleKeyDown}
			sx={{ position: "relative", cursor: disabled ? "default" : "pointer", ...formControlSx }}
		>
			<InputLabel id={`system-dropdown-label-${label}`} shrink={displayEmpty || undefined} sx={{ "& .MuiFormLabel-asterisk": { color: "error.main" } }}>
				{label}
			</InputLabel>
			<Select
				readOnly
				displayEmpty={displayEmpty}
				notched={displayEmpty || undefined}
				open={false}
				multiple={multiple}
				value={multiple ? valueIds : (valueIds[0] ?? "")}
				renderValue={renderDisplay as (value: unknown) => ReactNode}
				label={label}
				// The shell's own `open` is always false (our panel is a separate Popper), so MUI would report
				// aria-expanded="false". SelectDisplayProps is spread AFTER MUI's own aria attrs, so these win
				// and correctly reflect OUR open state + point aria-controls at our listbox (L10).
				SelectDisplayProps={{ "aria-expanded": open, "aria-haspopup": "listbox", "aria-controls": open ? controlsId : undefined }}
				IconComponent={(iconProps) => (clearable && hasSelection ? <span /> : <Caret open={open} className={iconProps.className} />)}
				endAdornment={clearButton || undefined}
				sx={{
					cursor: "pointer",
					"& .MuiSelect-select": { cursor: "pointer" },
					"&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha(INK.SECONDARY, 0.25) },
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: `1px solid ${alpha(INK.SECONDARY, 0.8)}` },
					...selectSx,
				}}
			>
				{valueIds.map((valueId) => (
					<MenuItem key={valueId} value={valueId} sx={{ display: "none" }} />
				))}
			</Select>
			{errorText && <FormHelperText>{errorText}</FormHelperText>}
		</FormControl>
	);
});
